import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { getHolidayAllowanceOverview } from '../services/HolidayAllowanceService';
import '../styles/EmployeeHours.css';

const THIS_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [THIS_YEAR, THIS_YEAR - 1, THIS_YEAR - 2];

function toMins(hhmm) {
    if (!hhmm) return 0;
    const [h, m] = hhmm.split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
}

function fmtHours(hhmm) {
    if (!hhmm || hhmm === '00:00') return '–';
    const [h, m] = hhmm.split(':').map(Number);
    if ((m || 0) === 0) return `${h}h`;
    return `${h}h ${m}m`;
}

function fmtOvertime(minutes) {
    if (minutes === 0) return '0h';
    const sign = minutes < 0 ? '−' : '+';
    const abs  = Math.abs(minutes);
    const h    = Math.floor(abs / 60);
    const m    = abs % 60;
    return m === 0 ? `${sign}${h}h` : `${sign}${h}h ${m}m`;
}

function SummaryCard({ label, value, sub, accent }) {
    return (
        <div className="eh-summary-card" style={{ borderTopColor: accent }}>
            <div className="eh-summary-label">{label}</div>
            <div className="eh-summary-value">{value}</div>
            {sub && <div className="eh-summary-sub">{sub}</div>}
        </div>
    );
}

export default function EmployeeHours() {
    const navigate = useNavigate();
    const [year, setYear]           = useState(THIS_YEAR);
    const [data, setData]           = useState([]);
    const [loading, setLoading]     = useState(true);
    const [error, setError]         = useState(null);
    const [search, setSearch]       = useState('');
    const [allowanceMap, setAllowanceMap] = useState({});

    const load = useCallback((y) => {
        setLoading(true);
        setError(null);
        api.get(`/Stats/admin?year=${y}`)
            .then(res => { setData(res.data); setLoading(false); })
            .catch(() => { setError('Daten konnten nicht geladen werden.'); setLoading(false); });
        getHolidayAllowanceOverview(y)
            .then(rows => {
                const map = {};
                rows.forEach(r => { map[r.user_id] = r; });
                setAllowanceMap(map);
            })
            .catch(() => setAllowanceMap({}));
    }, []);

    useEffect(() => { load(year); }, [year, load]);

    const filtered = data.filter(e =>
        !search || e.name.toLowerCase().includes(search.toLowerCase())
    );

    const totalHoursYear = filtered.reduce((s, e) => s + toMins(e.hours_year), 0);
    const totalSickDays  = filtered.reduce((s, e) => s + (e.sick_days || 0), 0);
    const totalVacDays   = filtered.reduce((s, e) => s + (e.vacation_days || 0), 0);
    const totalOtMins    = filtered.reduce((s, e) => s + (e.overtime_minutes || 0), 0);

    const fmtTotalHours = () => {
        const h = Math.floor(totalHoursYear / 60);
        const m = totalHoursYear % 60;
        return m === 0 ? `${h}h` : `${h}h ${m}m`;
    };

    const handleRowClick = (emp) => {
        navigate(`/employee-hours/${emp.user_id}`, {
            state: { name: emp.name, username: emp.username },
        });
    };

    return (
        <div className="eh-container">

            <div className="eh-hero">
                <div>
                    <h2>Mitarbeiter Stunden</h2>
                    <p className="eh-hero-sub">Arbeitszeiten, Überstunden und Abwesenheiten im Überblick</p>
                </div>
                <div className="eh-hero-controls">
                    <input
                        className="eh-search"
                        type="text"
                        placeholder="Mitarbeiter suchen…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                    <select className="eh-year-select" value={year} onChange={e => setYear(Number(e.target.value))}>
                        {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>
            </div>

            <div className="eh-summary-grid">
                <SummaryCard label="Mitarbeiter"           value={loading ? '…' : filtered.length}     sub="aktive Konten"                accent="#2563EB" />
                <SummaryCard label={`Gesamtstunden ${year}`} value={loading ? '…' : fmtTotalHours()}   sub="über alle Mitarbeiter"         accent="#1E40AF" />
                <SummaryCard
                    label={`Überstunden ${year}`}
                    value={loading ? '…' : fmtOvertime(totalOtMins)}
                    sub="kumuliert alle Mitarbeiter"
                    accent={totalOtMins >= 0 ? '#059669' : '#DC2626'}
                />
                <SummaryCard label={`Krankentage ${year}`}  value={loading ? '…' : totalSickDays}      sub="erfasste Krankheitstage"       accent="#DC2626" />
                <SummaryCard label={`Urlaubstage ${year}`}  value={loading ? '…' : totalVacDays}       sub="genommene Urlaubstage"         accent="#059669" />
            </div>

            <div className="eh-table-card">
                {loading ? (
                    <p className="eh-state-msg">Lade Daten…</p>
                ) : error ? (
                    <p className="eh-state-msg eh-error">{error}</p>
                ) : filtered.length === 0 ? (
                    <p className="eh-state-msg">Keine Mitarbeiter gefunden.</p>
                ) : (
                    <table className="eh-table">
                        <thead>
                            <tr>
                                <th>Mitarbeiter</th>
                                <th className="eh-th-num">Heute</th>
                                <th className="eh-th-num">Diesen Monat</th>
                                <th className="eh-th-num">Jahr {year}</th>
                                <th className="eh-th-num">Überstunden {year}</th>
                                <th className="eh-th-num">Krankentage</th>
                                <th className="eh-th-num">Urlaubstage</th>
                                <th className="eh-th-num">Resturlaub</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(emp => {
                                const otMin = emp.overtime_minutes || 0;
                                return (
                                    <tr
                                        key={emp.user_id}
                                        className="eh-row-clickable"
                                        onClick={() => handleRowClick(emp)}
                                        title="Details anzeigen"
                                    >
                                        <td>
                                            <div className="eh-name">{emp.name}</div>
                                            <div className="eh-username">{emp.username}</div>
                                        </td>
                                        <td className="eh-td-num">
                                            <span className="eh-hours">{fmtHours(emp.hours_today)}</span>
                                        </td>
                                        <td className="eh-td-num">
                                            <span className="eh-hours">{fmtHours(emp.hours_month)}</span>
                                        </td>
                                        <td className="eh-td-num">
                                            <span className="eh-hours eh-hours-year">{fmtHours(emp.hours_year)}</span>
                                        </td>
                                        <td className="eh-td-num">
                                            <span className={`eh-overtime ${otMin > 0 ? 'eh-overtime--pos' : otMin < 0 ? 'eh-overtime--neg' : 'eh-overtime--zero'}`}>
                                                {fmtOvertime(otMin)}
                                            </span>
                                        </td>
                                        <td className="eh-td-num">
                                            <span className={`eh-badge ${emp.sick_days > 0 ? 'eh-badge-sick' : 'eh-badge-zero'}`}>
                                                {emp.sick_days} {emp.sick_days === 1 ? 'Tag' : 'Tage'}
                                            </span>
                                        </td>
                                        <td className="eh-td-num">
                                            <span className={`eh-badge ${emp.vacation_days > 0 ? 'eh-badge-vac' : 'eh-badge-zero'}`}>
                                                {emp.vacation_days} {emp.vacation_days === 1 ? 'Tag' : 'Tage'}
                                            </span>
                                        </td>
                                        <td className="eh-td-num">
                                            {(() => {
                                                const remaining = allowanceMap[emp.user_id]?.remaining ?? null;
                                                if (remaining === null) return (
                                                    <span
                                                        className="eh-allowance-unset"
                                                        title="Kein Urlaubskontingent für dieses Jahr festgelegt. Bitte in den Einstellungen hinterlegen."
                                                    >
                                                        Nicht gesetzt
                                                    </span>
                                                );
                                                return (
                                                    <span style={{ fontWeight: 600, color: remaining <= 0 ? '#DC2626' : 'inherit' }}>
                                                        {remaining} {remaining === 1 ? 'Tag' : 'Tage'}
                                                    </span>
                                                );
                                            })()}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

        </div>
    );
}
